/**
 * O PERFIL A PEDIR, para cada licença — o que o estado "sem permissão" (p.34)
 * escreve.
 *
 * A prova do marco do ciclo 1 (07/10/2026) achou o 403 caindo no estado de
 * erro: "Tente de novo; se continuar, avise o suporte". Tentar de novo não
 * resolve, e o suporte não concede licença. O manual manda dizer o perfil a
 * pedir ("Você não tem acesso a Custos. Peça ao administrador o perfil
 * Gestor."), e a recusa do servidor só traz a licença.
 *
 * ## De onde vem
 *
 * Da proposta de papéis aprovada pelo dono (decisão 22,
 * `backend/src/autorizacao/proposta-de-papeis.ts`): é ela que cada empresa
 * recebe no primeiro dia. O que vale em execução é o papel da empresa, que a
 * governança troca sem deploy — por isso a frase diz o perfil da PROPOSTA, e o
 * administrador continua sendo quem concede. O teste
 * (`perfil-da-licenca.test.ts`) compara esta tabela com a proposta e com o
 * catálogo: licença nova sem linha aqui reprova.
 *
 * O administrador tem tudo e não entra nas listas; a licença que só ele tem
 * (as de gerir, os estornos) pede o perfil Administrador.
 */

export type CodigoDoPerfil =
  | 'diretoria'
  | 'gestor-operacional'
  | 'planejador-manutencao'
  | 'oficina'
  | 'almoxarifado'
  | 'compras'
  | 'controladoria'
  | 'operador'
  /* De FORA da empresa (0123): a frase do "sem permissao" precisa saber
     nomea-lo, porque quem administra concede este papel como concede os
     outros — e a consequencia dele e maior. */
  | 'posto-parceiro';

/** O nome que a tela escreve — o mesmo do teste `src/mobile/perfis.test.ts`. */
export const NOME_DO_PERFIL: Readonly<Record<CodigoDoPerfil | 'administrador', string>> = {
  diretoria: 'Diretoria',
  'gestor-operacional': 'Gestor operacional',
  'planejador-manutencao': 'Planejador de manutenção',
  oficina: 'Oficina',
  almoxarifado: 'Almoxarifado',
  compras: 'Compras',
  controladoria: 'Controladoria',
  operador: 'Operador',
  'posto-parceiro': 'Posto parceiro',
  administrador: 'Administrador',
};

/**
 * Licença -> os perfis da proposta que a têm.
 *
 * FORA DOIS, e os dois por motivos diferentes:
 *
 * - o **Administrador**, que tem todas — nomeá-lo em cada linha faria a frase
 *   do "sem permissão" terminar sempre com ele, e ela existe para dizer a quem
 *   pedir, não para listar quem já pode;
 * - o **Posto parceiro** (0123), que é de gente de FORA da empresa. Ele tem
 *   `abastecimento.registrar`, `combustivel.ler` e `equipamento.ler`, mas
 *   mandar um funcionário sem permissão "pedir o perfil Posto parceiro" seria
 *   mandá-lo pedir um acesso de terceiro, recortado a um fornecedor. A frase
 *   deve apontar o perfil que resolve o problema dele, e esse não resolve.
 */
export const PERFIS_DA_LICENCA: Readonly<Record<string, readonly CodigoDoPerfil[]>> = {
  /* Vazio = só o Administrador, e aqui isso é a decisão: conferir não é lançar.
     Quem deve conferir no dia a dia é cadastro pela governança, não deploy. */
  'abastecimento.conferir': [],
  'abastecimento.estornar': [],
  /* Vazio, e aqui é a decisão do dono (26/10/2026): *"só administrador mais
     ninguém"*. Nenhum perfil do guia a recebe — e é isto que faz a frase do
     "sem permissão" mandar pedir ao administrador, que é quem de fato pode. */
  'abastecimento.excluir': [],
  'abastecimento.registrar': ['almoxarifado', 'operador'],
  'acesso.convidar': [],
  'alocacao.gerir': ['gestor-operacional'],
  'alocacao.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'oficina', 'controladoria'],
  'aplicacao.validar': ['planejador-manutencao', 'almoxarifado'],
  'apontamento.conferir': ['gestor-operacional'],
  'apontamento.registrar': ['oficina', 'operador'],
  'apontamento_hora.estornar': [],
  'aptidao.gravar': ['gestor-operacional'],
  'auditoria.ler': ['controladoria'],
  'cadastro.gerir': ['compras'],
  'cadastro.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'compras', 'controladoria'],
  'catalogo.gerir': [],
  'catalogo.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'oficina', 'almoxarifado', 'compras', 'controladoria'],
  'checklist.gravar': ['planejador-manutencao'],
  'checklist.publicar': ['planejador-manutencao'],
  'cobranca.conciliar': ['controladoria'],
  'combustivel.gerir': [],
  'combustivel.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'oficina', 'almoxarifado', 'compras', 'controladoria', 'operador'],
  'compras.ler': ['diretoria', 'planejador-manutencao', 'almoxarifado', 'compras', 'controladoria'],
  'custo.configurar': ['controladoria'],
  'custo.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'controladoria'],
  'custo_indireto.estornar': [],
  'custo_indireto.lancar': ['controladoria'],
  'depreciacao.gravar': ['controladoria'],
  'documento.emitir': ['controladoria'],
  'documento.receber': ['controladoria'],
  'documento_de_pessoa.gravar': [],
  'documento_de_pessoa.ler': ['diretoria', 'gestor-operacional'],
  'documento_tecnico.gerir': [],
  'documento_tecnico.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'oficina', 'controladoria'],
  'documento_tecnico.revogar': [],
  'documento_tecnico.validar': ['planejador-manutencao'],
  'equipamento.gerir': [],
  'equipamento.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'oficina', 'almoxarifado', 'compras', 'controladoria', 'operador'],
  'estoque.ajustar': ['almoxarifado'],
  'estoque.gerir': ['almoxarifado'],
  'estoque.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'oficina', 'almoxarifado', 'compras', 'controladoria'],
  'estoque.movimentar': ['almoxarifado'],
  'financeiro.ler': ['diretoria', 'controladoria'],
  'governanca.gerir': [],
  'governanca.ler': ['diretoria'],
  'ia.auditar': ['planejador-manutencao'],
  'ia.perguntar': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'oficina'],
  'impedimento.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao'],
  'inspecao.gravar': ['oficina', 'operador'],
  'inspecao.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'oficina', 'operador'],
  'lubrificante.registrar': ['oficina', 'almoxarifado'],
  'manutencao.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'oficina', 'controladoria'],
  'medicao.aprovar': [],
  'medicao.gravar': ['controladoria'],
  'motivo_de_parada.gerir': ['gestor-operacional'],
  'movimento_tanque.corrigir': ['almoxarifado', 'compras'],
  'movimento_tanque.estornar': [],
  'movimento_tanque.excluir': [],
  'ocorrencia.encerrar': ['planejador-manutencao', 'oficina'],
  'operacao.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'oficina', 'controladoria', 'operador'],
  'ordem_abastecimento.cancelar': ['gestor-operacional'],
  'ordem_abastecimento.emitir': ['gestor-operacional'],
  'os.abrir': ['gestor-operacional', 'planejador-manutencao', 'oficina'],
  'os.executar': ['oficina'],
  'os.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'oficina', 'controladoria'],
  'os.liberar': ['oficina'],
  'pedido.aprovar': ['compras'],
  'pedido.gravar': ['compras'],
  'piloto.gerir': ['gestor-operacional'],
  'piloto.ler': ['diretoria', 'gestor-operacional'],
  'plano.gerir': ['planejador-manutencao'],
  'plano.publicar': ['planejador-manutencao'],
  'plano_de_contas.gerir': ['controladoria'],
  'prontuario.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'oficina', 'controladoria'],
  'realizacao.registrar': ['planejador-manutencao'],
  'recebimento.gravar': ['almoxarifado'],
  'receita.ler': ['diretoria', 'controladoria'],
  'regra_do_contrato.gravar': [],
  'requisicao.gravar': ['planejador-manutencao', 'almoxarifado', 'compras'],
  'situacao.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'oficina', 'controladoria'],
  'situacao.registrar': ['gestor-operacional', 'planejador-manutencao', 'oficina', 'operador'],
  'tanque.balanco': ['almoxarifado'],
  'tanque.medir': ['almoxarifado'],
  'tanque.receber': ['almoxarifado', 'compras'],
  'tanque.transferir': ['almoxarifado'],
  /* 0103: a telemetria (módulo 17). Gerir — a integração, a chave e o
     vínculo — é só do Administrador. */
  'telemetria.conferir': ['gestor-operacional'],
  'telemetria.gerir': [],
  'telemetria.ler': ['diretoria', 'gestor-operacional', 'planejador-manutencao', 'oficina'],
  /* 0105: a preditiva (módulo 18). Gerir e adotar o modelo e decidir a
     proposta técnica são só do administrador, até o dono decidir. */
  'preditiva.ler': ['planejador-manutencao', 'oficina'],
  'falha.classificar': ['planejador-manutencao', 'oficina'],
  'catalogo_de_falha.gerir': ['planejador-manutencao'],
  'investigacao.gravar': ['planejador-manutencao'],
  'modelo_preditivo.gerir': [],
  'modelo_preditivo.adotar': [],
  'proposta_tecnica.decidir': [],
  /* 0104: a troca com o ERP (módulo 04). Configurar, aprovar o mestre,
     importar e exportar são só do Administrador, até o dono decidir. */
  'integracao.ler': ['diretoria', 'controladoria'],
  'integracao.configurar': [],
  'integracao.aprovar_mestre': [],
  'integracao.importar': [],
  'integracao.exportar': [],
  'integracao.conciliar': ['controladoria'],
  'integracao.contingencia': [],
  'titulo.gravar': [],
  /* 0106: o orçamento (módulo 19). Aprovar a versão e mudar os limiares
     do desvio é só do Administrador, até o dono decidir. */
  'orcamento.ler': ['diretoria', 'controladoria'],
  'orcamento.gravar': ['controladoria'],
  'orcamento.aprovar': [],
  'desvio.explicar': ['controladoria'],
  'caixa.informar_saldo': ['controladoria'],
  /* 0107: a renovação da frota (módulo 20). Aprovar o investimento e o
     critério (e mudar os parâmetros) é só do Administrador, até o dono decidir. */
  'renovacao.ler': ['diretoria', 'gestor-operacional', 'controladoria'],
  'estudo.gravar': ['gestor-operacional', 'controladoria'],
  'investimento.propor': ['gestor-operacional', 'controladoria'],
  'investimento.aprovar': [],
  'criterio.aprovar': [],
  'turno.gerir': [],
  'valor_hora.gravar': ['controladoria'],
};

/** "Gestor operacional", "Almoxarifado ou Operador", "Diretoria, Gestor operacional ou Oficina". */
export function perfilAPedir(licenca: string): string {
  const nomes = (PERFIS_DA_LICENCA[licenca] ?? []).map((p) => NOME_DO_PERFIL[p]);
  if (nomes.length === 0) return NOME_DO_PERFIL.administrador;
  if (nomes.length === 1) return nomes[0] ?? NOME_DO_PERFIL.administrador;
  return `${nomes.slice(0, -1).join(', ')} ou ${nomes[nomes.length - 1] ?? ''}`;
}

/**
 * A licença que a recusa 403 do servidor nomeia. O portão escreve
 * "Peça a quem administra o sistema a permissão “…” (prontuario.ler) para o seu
 * perfil." (`backend/src/autorizacao/portao.ts`); a licença é o código entre
 * parênteses.
 */
export function licencaDaRecusa(comoResolver: string | null | undefined): string | null {
  const achada = /\(([a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)+)\)/.exec(comoResolver ?? '');
  return achada?.[1] ?? null;
}

/** O erro é uma recusa por falta de licença (403), venha da camada que vier. */
export function ehSemPermissao(erro: unknown): erro is { readonly status: 403; readonly comoResolver?: string } {
  return typeof erro === 'object' && erro !== null && 'status' in erro && (erro as { readonly status: unknown }).status === 403;
}

/**
 * A frase do sem permissão a partir da recusa 403: o perfil a pedir, e o que
 * ele libera — as mesmas palavras do `SemPermissao` da mesa e do celular.
 * Quando a recusa não nomeia a licença, vale a frase do próprio servidor.
 */
export function fraseDoSemPermissao(erro: { readonly comoResolver?: string } | null | undefined, libera: string): string {
  const licenca = licencaDaRecusa(erro?.comoResolver);
  if (licenca) return `Peça ao administrador da sua empresa o perfil ${perfilAPedir(licenca)}: é ele que libera ${libera}.`;
  return erro?.comoResolver ?? `Peça ao administrador da sua empresa o perfil que libera ${libera}.`;
}

/** "Os documentos" -> "os documentos": o sujeito da frase de erro vira o objeto do "libera". */
export function comoObjeto(oQue: string): string {
  return `${oQue.charAt(0).toLocaleLowerCase('pt-BR')}${oQue.slice(1)}`;
}
